import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import MumzoLogo from "@/components/MumzoLogo";

const genId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const SHEETDB_URL = "https://sheetdb.io/api/v1/9g8mltqv32p3l";

const initial = {
  name: "",
  phone: "",
  address: "",
  pincode: "",
  baby_name: "",
  baby_age: "",
};

const HYD_PIN = /^(500|501)\d{3}$/;
// Indian mobile — 10 digits, first digit 6/7/8/9
const PHONE_RE = /^[6-9]\d{9}$/;

export default function WaitlistSection() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const phone = form.phone.trim();
  const phoneTouched = phone.length > 0;
  const phoneValid = PHONE_RE.test(phone);

  const pin = form.pincode.trim();
  const pinValid = /^\d{6}$/.test(pin);
  const pinIsHyd = HYD_PIN.test(pin);
  const isFuture = pinValid && !pinIsHyd;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pincode) {
      toast.error("Please fill in your name, phone and pincode.");
      return;
    }
    if (!phoneValid) {
      toast.error("Enter a valid 10-digit mobile number.");
      return;
    }
    if (!pinValid) {
      toast.error("Please enter a valid 6-digit pincode.");
      return;
    }
    // For Hyderabad — require the full form. For future-cities — name + email + pincode is enough.
    if (pinIsHyd && (!form.address || !form.baby_name || !form.baby_age)) {
      toast.error("Please fill in every field, mama.");
      return;
    }
    setLoading(true);
    try {
      const entry = {
        id: genId(),
        name: form.name.trim(),
        phone,
        address: (pinIsHyd ? form.address : form.address || "—").trim(),
        pincode: pin,
        baby_name: (pinIsHyd ? form.baby_name : form.baby_name || "—").trim(),
        baby_age: (pinIsHyd ? form.baby_age : form.baby_age || "—").trim(),
        is_hyderabad: pinIsHyd,
        created_at: new Date().toISOString(),
      };

      await axios.post(SHEETDB_URL, { data: entry });

      const message = pinIsHyd
        ? "Welcome to the Mumzo family. We'll see you soon in Hyderabad."
        : "We're not in your city yet, mama, but you're on our future-cities list. We'll ping you the moment Mumzo comes near you.";

      setSuccess({ is_hyderabad: pinIsHyd, message });
      toast.dismiss();
      toast.success("You're on the list!");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="relative py-24 md:py-32 bg-blush">
      <div className="max-w-2xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <span className="kicker">Founding Parents</span>
          <h2 className="mt-6 font-editorial text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Be one of our <span className="italic text-pinkDeep">founding parents.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-foreground/70 max-w-md mx-auto leading-relaxed">
            Drop your details to get early access, someone from the team
            will reach out on next steps.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="mt-12"
        >
          {success ? (
            <div
              data-testid="waitlist-success"
              className="p-10 md:p-12 rounded-3xl bg-white border border-border/70 text-center"
            >
              <div className="mx-auto mb-6 text-pinkDeep">
                <MumzoLogo variant="mark" height={56} />
              </div>
              <h3 className="font-editorial text-3xl md:text-4xl leading-tight" data-testid="waitlist-success-title">
                {success.is_hyderabad
                  ? "Thank you for filling the details."
                  : "Thanks for signing up!"}
              </h3>
              <p className="mt-4 text-foreground/70 max-w-md mx-auto" data-testid="waitlist-success-message">
                {success.is_hyderabad
                  ? "Someone in our team will reach out soon."
                  : "We're not live in your city just yet, but we'll let you know the moment Mumzo launches there."}
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="waitlist-form"
              className="p-8 md:p-10 rounded-3xl bg-white border border-border/70"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <label className="flex flex-col gap-2 md:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-foreground/60">Your name</span>
                  <input
                    data-testid="waitlist-name"
                    type="text"
                    placeholder="e.g. Ananya Reddy"
                    value={form.name}
                    onChange={update("name")}
                    className="mumzo-input"
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 md:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-foreground/60">Mobile number</span>
                  <div className="flex items-center gap-2">
                    <span className="px-4 py-3 rounded-2xl bg-blush text-foreground/80 text-sm font-medium select-none">+91</span>
                    <input
                      data-testid="waitlist-phone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      autoComplete="tel-national"
                      placeholder="10-digit mobile"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                      className={`mumzo-input flex-1 ${phoneTouched && !phoneValid ? "!border-destructive !shadow-none" : ""}`}
                      required
                      aria-invalid={phoneTouched && !phoneValid}
                    />
                  </div>
                  {phoneTouched && !phoneValid && (
                    <span data-testid="phone-error" className="text-xs text-destructive px-1">
                      Enter a valid 10-digit Indian mobile number.
                    </span>
                  )}
                </label>
                <label className="flex flex-col gap-2 md:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-foreground/60">Pincode</span>
                  <input
                    data-testid="waitlist-pincode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="e.g. 500033"
                    value={form.pincode}
                    onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                    className="mumzo-input"
                    required
                  />
                </label>
              </div>

              <AnimatePresence mode="wait">
                {isFuture && (
                  <motion.div
                    key="future"
                    initial={{ opacity: 0, y: -8, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -8, height: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    data-testid="pin-hint-other"
                    className="mt-5 overflow-hidden"
                  >
                    <div className="p-5 rounded-2xl bg-pinkSoft border border-rose/40 flex gap-3">
                      <span className="text-xl leading-none" aria-hidden>✈</span>
                      <div>
                        <p className="font-editorial text-lg leading-snug">
                          We're only in Hyderabad right now.
                        </p>
                        <p className="mt-1.5 text-sm text-foreground/70 leading-relaxed">
                          Leave your name, email and pincode, we'll notify you
                          the moment Mumzo launches in your city. No baby details
                          needed.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
                {pinValid && pinIsHyd && (
                  <motion.div
                    key="hyd"
                    initial={{ opacity: 0, y: -8, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: "auto" }}
                    exit={{ opacity: 0, y: -8, height: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    data-testid="pin-hint-hyderabad"
                    className="mt-5 overflow-hidden"
                  >
                    <div className="p-5 rounded-2xl bg-blush border border-rose/40 flex gap-3">
                      <span className="text-xl leading-none text-pinkDeep" aria-hidden>♡</span>
                      <div>
                        <p className="font-editorial text-lg leading-snug text-pinkDeep">
                          You're in Hyderabad, we launch here first.
                        </p>
                        <p className="mt-1.5 text-sm text-foreground/70 leading-relaxed">
                          A few more details so we can pack your first delivery
                          with care.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence initial={false}>
                {(!pinValid || pinIsHyd) && (
                  <motion.div
                    key="hyd-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="mt-5 grid md:grid-cols-2 gap-4">
                      <label className="flex flex-col gap-2 md:col-span-2">
                        <span className="text-xs uppercase tracking-widest text-foreground/60">Delivery address</span>
                        <input
                          data-testid="waitlist-address"
                          type="text"
                          placeholder="Flat, building, area"
                          value={form.address}
                          onChange={update("address")}
                          className="mumzo-input"
                          required={pinIsHyd || !pinValid}
                        />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="text-xs uppercase tracking-widest text-foreground/60">Baby's name</span>
                        <input
                          data-testid="waitlist-baby-name"
                          type="text"
                          placeholder="e.g. Kabir"
                          value={form.baby_name}
                          onChange={update("baby_name")}
                          className="mumzo-input"
                          required={pinIsHyd || !pinValid}
                        />
                      </label>
                      <label className="flex flex-col gap-2">
                        <span className="text-xs uppercase tracking-widest text-foreground/60">Baby's age</span>
                        <input
                          data-testid="waitlist-baby-age"
                          type="text"
                          placeholder="e.g. 6 months"
                          value={form.baby_age}
                          onChange={update("baby_age")}
                          className="mumzo-input"
                          required={pinIsHyd || !pinValid}
                        />
                      </label>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={loading || (phoneTouched && !phoneValid)}
                data-testid="waitlist-submit"
                className="mt-8 mumzo-btn text-base w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Joining…" : isFuture ? "Notify me in my city" : "Get early access →"}
              </button>

              <p className="mt-5 text-sm text-foreground/70 text-center">
                Once you fill the form, someone from the team will reach out
                on next steps.
              </p>
              <p className="mt-2 text-xs text-foreground/50 text-center">
                By joining, you agree to hear from us about Mumzo's launch.
                We'll never share your details.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
